/**
 * Footballer Quiz API helpers. Daily attempt flow keyed by FOOTBALLER:
 *   - start  → guessed rows so far + streak (reveal only once the attempt is over)
 *   - search → autocomplete players for the guess input
 *   - guess  → compare a player id to the mystery (or finish the day)
 * The mystery's identity is never returned until the attempt is won or lost.
 */

import type { useApiClient } from '../../../helpers/apiClient.helper';
import { API_ENDPOINTS } from '../../../constants/api';
import { FOOTBALLER_GAME_KEY, type FootballerMark } from '../constants/footballerQuiz.constants';

type ApiClient = ReturnType<typeof useApiClient>;

/** One attribute tile within a guessed row. `country` is a country NAME — the client renders its flag as a
 *  CDN image (Android can't draw subdivision flag emoji). */
export interface FootballerNatCell { country: string | null; mark: FootballerMark }
export interface FootballerLeagueCell { short: string; country: string | null; division: number; mark: FootballerMark }
export interface FootballerTeamCell { club: string | null; mark: FootballerMark }
export interface FootballerPosCell { bucket: string | null; mark: FootballerMark }
export interface FootballerAgeCell { value: number | null; mark: FootballerMark; arrow: string }

/** A guessed player's row: the guessed player's cells + their per-attribute marks (carries no answer). */
export interface FootballerGuessRow {
  playerId: string;
  name: string;
  nat: FootballerNatCell;
  league: FootballerLeagueCell;
  team: FootballerTeamCell;
  pos: FootballerPosCell;
  age: FootballerAgeCell;
}

/** An autocomplete candidate. The flag is rendered client-side from `nationality` (a country name). */
export interface FootballerSuggestion {
  id: string;
  name: string;
  nationality: string | null;
  club: string | null;
  position: string | null;
}

/** The mystery reveal — only present once the attempt is over. */
export interface FootballerReveal {
  name: string;
  nationality: string | null;
  club: string | null;
  league: string | null;
  leagueShort: string;
  position: string | null;
  age: number | null;
}

export type FootballerStatus = 'IN_PROGRESS' | 'WON' | 'LOST';

export interface FootballerRun {
  maxGuesses: number;
  guesses: FootballerGuessRow[];
  guessesUsed: number;
  guessesLeft: number;
  status: FootballerStatus;
  streak: number;
  /** Present only when the attempt is already over. */
  reveal?: FootballerReveal;
  won?: boolean;
  share?: string;
}

/** A guess that did not end the attempt. */
export interface FootballerGuessOngoing {
  done: false;
  guess: Omit<FootballerGuessRow, 'playerId'>;
  status: 'IN_PROGRESS';
  guessesUsed: number;
  guessesLeft: number;
}

/** A guess that ended the attempt (win or loss). */
export interface FootballerGuessComplete {
  done: true;
  guess: Omit<FootballerGuessRow, 'playerId'>;
  status: 'WON' | 'LOST';
  guessesUsed: number;
  guessesLeft: number;
  streak: number;
  won: boolean;
  reveal: FootballerReveal;
  share: string;
  coinsAwarded: number;
  coins?: number;
}

export type FootballerGuessResult = FootballerGuessOngoing | FootballerGuessComplete;

export async function startFootballerRun(apiClient: ApiClient): Promise<FootballerRun> {
  const response = await apiClient<{ success: boolean; data: FootballerRun; message?: string }>(
    API_ENDPOINTS.games.roundStart(FOOTBALLER_GAME_KEY),
    { method: 'POST', data: {} },
  );
  const payload = response.data;
  if (!payload.success) throw new Error(payload.message ?? "Could not start today's game");
  return payload.data;
}

export async function searchFootballers(apiClient: ApiClient, q: string): Promise<FootballerSuggestion[]> {
  const response = await apiClient<{ success: boolean; data: { players: FootballerSuggestion[] }; message?: string }>(
    API_ENDPOINTS.games.roundSearch(FOOTBALLER_GAME_KEY),
    { method: 'POST', data: { q } },
  );
  const payload = response.data;
  if (!payload.success) throw new Error(payload.message ?? 'Search failed');
  return payload.data.players;
}

export async function guessFootballer(apiClient: ApiClient, playerId: string): Promise<FootballerGuessResult> {
  const response = await apiClient<{ success: boolean; data: FootballerGuessResult; message?: string }>(
    API_ENDPOINTS.games.roundGuess(FOOTBALLER_GAME_KEY),
    { method: 'POST', data: { playerId } },
  );
  const payload = response.data;
  if (!payload.success) throw new Error(payload.message ?? 'Could not submit your guess');
  return payload.data;
}
